import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const pkg = params.get("pkg");
  const reportId = params.get("report_id");
  const [state, setState] = useState("checking"); // checking | paid | failed
  const attempts = useRef(0);

  const dest = pkg === "report_unlock" && reportId ? `/results/${reportId}` : "/success-kit";
  const destLabel = pkg === "report_unlock" ? "View my report" : "Open my Success Kit";

  useEffect(() => {
    if (!sessionId) { setState("failed"); return; }
    let active = true;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (!active) return;
        if (data.payment_status === "paid") { setState("paid"); return; }
        if (data.status === "expired") { setState("failed"); return; }
      } catch { /* keep retrying */ }
      if (attempts.current++ < 12) setTimeout(poll, 2000);
      else if (active) setState("failed");
    };
    poll();
    return () => { active = false; };
  }, [sessionId]);

  return (
    <div className="mx-auto flex min-h-[70vh] max-w-lg flex-col items-center justify-center text-center" data-testid="payment-success-page">
      {state === "checking" && (
        <>
          <Loader2 className="h-10 w-10 animate-spin text-primary" />
          <h1 className="mt-6 font-heading text-2xl font-semibold tracking-tight">Confirming your payment…</h1>
          <p className="mt-2 text-sm text-slate-500">This only takes a moment.</p>
        </>
      )}

      {state === "paid" && (
        <>
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-green-50">
            <CheckCircle2 className="h-9 w-9 text-green-600" />
          </div>
          <h1 className="mt-6 font-heading text-2xl font-semibold tracking-tight" data-testid="payment-success-title">Payment successful</h1>
          <p className="mt-2 text-slate-600" data-testid="payment-success-message">Payment successful. Your report is being generated.</p>
          <Button className="mt-8 gap-2" onClick={() => navigate(dest)} data-testid="payment-success-continue">
            {destLabel} <ArrowRight className="h-4 w-4" />
          </Button>
        </>
      )}

      {state === "failed" && (
        <>
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-amber-50">
            <XCircle className="h-9 w-9 text-amber-600" />
          </div>
          <h1 className="mt-6 font-heading text-2xl font-semibold tracking-tight">We couldn't confirm your payment</h1>
          <p className="mt-2 text-slate-600">If you completed payment, your access will unlock shortly — please check your report in a minute. Otherwise you can try again.</p>
          <div className="mt-8 flex gap-3">
            <Button variant="outline" onClick={() => navigate(reportId ? `/results/${reportId}` : "/pricing")}>Back</Button>
            <Button onClick={() => navigate("/pricing")}>Try again</Button>
          </div>
        </>
      )}
    </div>
  );
}
